import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as actions from './InternalAdminsTab.ducks';

import Loading from 'zignal-landscape/lib/Loading';
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import { Light } from 'themes';
import SearchInput from 'zignal-landscape/lib/SearchInput';
import Heading from 'zignal-landscape/lib/Heading';
import WarningIcon from 'zignal-landscape/lib/Icon/WarningIcon';
import themeable from 'zignal-landscape/lib/themeable';

import InternalAdminsTable from './components/InternalAdminsTable';
import InternalAdminsTableFooter from './components/InternalAdminsTableFooter';
import * as utils from './utils';

const mapStateToProps = (
  (state) => ({
    internalAdminUsers: state.InternalAdminsTab.internalAdminUsers,
    isFetching: state.InternalAdminsTab.isFetching,
    error: state.InternalAdminsTab.error,
    sortBy: state.InternalAdminsTab.sortBy,
    sortOrder: state.InternalAdminsTab.sortOrder,
    filterTerm: state.InternalAdminsTab.filterTerm,
    selectedPageNumber: state.InternalAdminsTab.selectedPageNumber,
    elementsPerPage: state.InternalAdminsTab.elementsPerPage,
    paginatedUsers: state.InternalAdminsTab.paginatedUsers,
    totalRecords: state.InternalAdminsTab.totalRecords,
    totalTablePages: state.InternalAdminsTab.totalTablePages,
    fromPageCount: state.InternalAdminsTab.fromPageCount,
    toPageCount: state.InternalAdminsTab.toPageCount,
    startPaginationRange: state.InternalAdminsTab.startPaginationRange,
    endPaginationRange: state.InternalAdminsTab.endPaginationRange,
    isInternalAdmin: state.AppUser.isInternalAdmin,
    user: state.AppUser.user,
  })
);

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

const DEFAULT_INTERNAL_ADMIN_USERS = [];
const DEFAULT_PAGINATED_USERS = [];
const DEFAULT_USER = {};

const InternalAdminsTab = ({
  styles,
  internalAdminUsers = DEFAULT_INTERNAL_ADMIN_USERS,
  isFetching = false,
  error = null,
  sortBy = 'username',
  sortOrder = 'asc',
  filterTerm = '',
  selectedPageNumber = 1,
  elementsPerPage = 50,
  paginatedUsers = DEFAULT_PAGINATED_USERS,
  totalRecords = 0,
  totalTablePages = 0,
  fromPageCount = 0,
  toPageCount = 0,
  startPaginationRange = 0,
  endPaginationRange = 0,
  isInternalAdmin = false,
  user = DEFAULT_USER,
  accountMap,
  fetchInternalAdmins,
  setFilterTerm,
  updatePaginatedTableData,
  clearState
}) => {
  // Check if user has Full Account Access
  const hasFullAccountAccess = user && user.fullAccountAccessEnabled === true;
  const hasAccess = isInternalAdmin && hasFullAccountAccess;

  // Create userMap from internalAdminUsers for name lookups
  const userMap = React.useMemo(() => {
    const map = {};
    if (internalAdminUsers && Array.isArray(internalAdminUsers)) {
      internalAdminUsers.forEach(u => {
        if (u.id && u.username) {
          map[u.id] = u;
        }
      });
    }
    return map;
  }, [internalAdminUsers]);

  const handlePaginatedTableDataUpdate = () => {
    // Filter users based on search term
    let filtered = internalAdminUsers;
    if (filterTerm) {
      filtered = utils.filterUsers(internalAdminUsers, filterTerm, accountMap, userMap);
    }

    // Sort users based on selected column and order
    const sorted = utils.sortUsers(filtered, sortBy, sortOrder, accountMap);

    // Get paginated data
    const paginationOptions = {
      selectedPageNumber,
      elementsPerPage
    };
    const paginatedData = utils.getPaginatedDataTable(sorted, paginationOptions);

    // Dispatch Redux action to update pagination state
    updatePaginatedTableData(paginatedData);
  };

  React.useEffect(() => {
    if (hasAccess) {
      fetchInternalAdmins();
    }
    return () => {
      clearState();
    };
  }, [hasAccess]);

  React.useEffect(() => {
    // Handle paginated table data update
    handlePaginatedTableDataUpdate();
  }, [internalAdminUsers, sortBy, sortOrder, filterTerm, selectedPageNumber, elementsPerPage]);

  const handleSearchChange = (val) => {
    setFilterTerm(val);
  };

  const handleItemsPerPageChange = (val) => {
    // Extract value from Select option object
    const itemsPerPage = val && val.value ? val.value : val;
    // Filter and sort users
    let filtered = internalAdminUsers;
    if (filterTerm) {
      filtered = utils.filterUsers(internalAdminUsers, filterTerm);
    }
    const sorted = utils.sortUsers(filtered, sortBy, sortOrder, accountMap);
    const paginatedData = utils.getPaginatedDataTable(sorted, {
      selectedPageNumber: 1,
      elementsPerPage: itemsPerPage
    });

    updatePaginatedTableData(paginatedData);
  };

  const handleSortChangeClick = (columnName) => {
    try {
      // Determine new sort order
      let newSortOrder = 'asc';
      if (columnName === sortBy) {
        // Same column clicked - toggle sort order
        newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      }
      let filtered = internalAdminUsers;
      if (filterTerm) {
        filtered = utils.filterUsers(internalAdminUsers, filterTerm, accountMap, userMap);
      }
      // Sort users with the new sort parameters
      const sorted = utils.sortUsers(filtered, columnName, newSortOrder, accountMap);

      // Get paginated data - reset to page 1 on sort change
      const paginationData = utils.getPaginatedDataTable(sorted, {
        selectedPageNumber: 1,
        elementsPerPage
      });

      // Dispatch updatePaginatedTableData with sort info included
      updatePaginatedTableData({
        ...paginationData,
        sortBy: columnName,
        sortOrder: newSortOrder
      });
    } catch (error) {
      console.error('Error in handleSortChangeClick:', error, error.stack);
    }
  };

  const handleTableNavChange = (pageNumber) => {
    // Filter and sort users
    let filtered = internalAdminUsers;
    if (filterTerm) {
      filtered = utils.filterUsers(internalAdminUsers, filterTerm);
    }
    const sorted = utils.sortUsers(filtered, sortBy, sortOrder);

    // Get paginated data with new page number
    const paginatedData = utils.getPaginatedDataTable(sorted, {
      selectedPageNumber: pageNumber,
      elementsPerPage
    });

    updatePaginatedTableData(paginatedData);
  };

  if (!hasAccess) {
    return (
      <ThemeProvider theme={Light}>
        <div className={styles.InternalAdminsContainer}>
          <Heading level={2}>Internal Admins</Heading>
          <div className={styles.accessDenied}>
            <span className={styles.WarningIcon}><WarningIcon width={40} height={40} /></span>
            <div className={styles.AccessDeniedMessage}>
              You do not have permission to access this section. Only Internal Admins with Full Account Access can manage Internal Admins.
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  const bodyContent = isFetching
    ? (
      <div key="InternalAdminsLoader" style={{textAlign: 'center', padding: '40px', position: 'relative', height: '80px'}}>
        <Loading />
      </div>
    ) : (
      <>
        <InternalAdminsTable
          users={paginatedUsers}
          sortOrder={sortOrder}
          sortBy={sortBy}
          accountMap={accountMap}
          userMap={userMap}
          handleSortChange={handleSortChangeClick}
        />
        <InternalAdminsTableFooter
          totalRecords={totalRecords}
          totalTablePages={totalTablePages}
          toPageCount={toPageCount}
          fromPageCount={fromPageCount}
          elementsPerPage={elementsPerPage}
          selectedPageNumber={selectedPageNumber}
          startPaginationRange={startPaginationRange}
          endPaginationRange={endPaginationRange}
          handleItemsPerPageChange={handleItemsPerPageChange}
          handleTableNavChange={handleTableNavChange}
        />
      </>
    );

  return (
    <ThemeProvider theme={Light}>
      <div className={styles.InternalAdminsContainer}>
        <Heading level={2}>Internal Admins</Heading>
        <br />
        <div className={styles.searchContainer}>
          <SearchInput
            data-cy="search-internal-admins"
            id="search-input-internal-admins"
            placeholder="Search by username, email, accounts..."
            size="normal"
            onChange={handleSearchChange}
            value={filterTerm}
            divider
          />
        </div>
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}
        <div>
          {bodyContent}
        </div>
      </div>
    </ThemeProvider>
  );
};

InternalAdminsTab.propTypes = {
  styles: PropTypes.object,
  internalAdminUsers: PropTypes.array,
  isFetching: PropTypes.bool,
  error: PropTypes.string,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  filterTerm: PropTypes.string,
  selectedPageNumber: PropTypes.number,
  elementsPerPage: PropTypes.number,
  paginatedUsers: PropTypes.array,
  totalRecords: PropTypes.number,
  totalTablePages: PropTypes.number,
  fromPageCount: PropTypes.number,
  toPageCount: PropTypes.number,
  startPaginationRange: PropTypes.number,
  endPaginationRange: PropTypes.number,
  isInternalAdmin: PropTypes.bool,
  user: PropTypes.object,
  accountMap: PropTypes.object,
  fetchInternalAdmins: PropTypes.func,
  setFilterTerm: PropTypes.func,
  updatePaginatedTableData: PropTypes.func,
  clearState: PropTypes.func,
};

export default connect(mapStateToProps, actions, mergeProps)(themeable(InternalAdminsTab));
